import { marked } from 'marked'
import DOMPurify from 'dompurify'

marked.setOptions({
  breaks: true,
  gfm: true
})

function splitIntoLines(md) {
  if (md.includes('\n')) return md.split('\n')
  
  const lines = []
  let pos = 0
  
  // Match table row pattern: | | content | |
  // Use non-greedy match to get individual rows
  const tableRowRegex = /\| \| [^\n]+? \| \|/g
  let match
  
  while ((match = tableRowRegex.exec(md)) !== null) {
    // Add any content before this table row
    if (match.index > pos) {
      lines.push(md.substring(pos, match.index))
    }
    
    // Add the table row
    lines.push(match[0])
    
    pos = tableRowRegex.lastIndex
  }
  
  // Add remaining content
  if (pos < md.length) {
    lines.push(md.substring(pos))
  }
  
  return lines.filter(l => l.length > 0)
}

function countCols(line) {
  return line.split('|').filter(c => c.trim() !== '').length
}

function isMeaningfulHeaderRow(line) {
  if (!isTableRow(line)) return false
  const cells = line.split('|').filter(c => c.trim() !== '')
  if (cells.length < 2) return false
  const nonEmpty = cells.filter(c => c.trim().length > 0)
  return nonEmpty.length >= 2
}

function isLikelyTableTitleOrDesc(line) {
  if (!isTableRow(line)) return false
  const cells = line.split('|').slice(1, -1)
  const nonEmptyCells = cells.filter(c => c.trim().length > 0)
  if (nonEmptyCells.length <= 1) return true
  const content = nonEmptyCells.map(c => c.trim()).join(' ')
  if (/^[一二三四五六七八九十\d]+[、.．]/.test(content.trim())) return true
  if (/^[（(]/.test(content.trim())) return true
  if (content.length > 80 && nonEmptyCells.length < 3) return true
  return false
}

function stripTableRowToText(line) {
  const cells = line.split('|').slice(1, -1)
  return cells.map(c => c.trim()).filter(c => c.length > 0).join(' ')
}

function normalizeTables(md) {
  const lines = splitIntoLines(md)
  const sepPositions = []
  for (let s = 0; s < lines.length - 1; s++) {
    const sp = lines[s].trim()
    if (isSepRow(sp) && s > 0) {
      sepPositions.push(s)
    }
  }
  const tableRegions = []
  const usedIndexes = new Set()
  for (const sepIdx of sepPositions) {
    if (usedIndexes.has(sepIdx)) continue
    const sepColCount = countCols(lines[sepIdx].trim())
    let headerIdx = -1
    for (let h = sepIdx - 1; h >= 0; h--) {
      const ht = lines[h].trim()
      if (!isTableRow(ht) || isSepRow(ht)) break
      const hc = countCols(ht)
      if (hc === sepColCount && isMeaningfulHeaderRow(ht)) {
        headerIdx = h
        break
      }
    }
    if (headerIdx === -1) continue
    let dataEnd = sepIdx + 1
    while (dataEnd < lines.length) {
      const dt = lines[dataEnd].trim()
      if (isSepRow(dt)) break
      if (!isTableRow(dt)) break
      const dc = countCols(dt)
      if (dc !== sepColCount && isLikelyTableTitleOrDesc(dt)) break
      dataEnd++
    }
    let regionStart = headerIdx
    for (let r = headerIdx - 1; r >= 0; r--) {
      if (usedIndexes.has(r)) break
      const rt = lines[r].trim()
      if (isTableRow(rt) && !isSepRow(rt)) {
        regionStart = r
      } else {
        break
      }
    }
    let regionDataEnd = dataEnd
    for (let r = dataEnd; r < lines.length; r++) {
      const rt = lines[r].trim()
      if (isSepRow(rt)) break
      if (!isTableRow(rt)) break
      const rc = countCols(rt)
      if (rc === sepColCount) {
        regionDataEnd = r + 1
      } else {
        break
      }
    }
    tableRegions.push({
      start: regionStart,
      headerIdx: headerIdx,
      sepIdx: sepIdx,
      sepColCount: sepColCount,
      dataEnd: dataEnd,
      regionEnd: regionDataEnd
    })
    for (let u = regionStart; u < regionDataEnd; u++) {
      usedIndexes.add(u)
    }
  }
  const out = []
  let i = 0
  while (i < lines.length) {
    const region = tableRegions.find(r => r.start === i)
    if (region) {
      for (let k = region.start; k < region.headerIdx; k++) {
        const kb = lines[k].trim()
        if (isLikelyTableTitleOrDesc(kb)) {
          out.push(stripTableRowToText(kb))
        } else {
          out.push(lines[k])
        }
      }
      const tableRows = [normalizeRow(lines[region.headerIdx].trim())]
      let stopBuilding = false
      let stopIdx = region.regionEnd
      for (let j = region.headerIdx + 1; j < region.regionEnd && !stopBuilding; j++) {
        if (j === region.sepIdx) continue
        const rowTrimmed = lines[j].trim()
        if (isSepRow(rowTrimmed)) continue
        const rc = countCols(rowTrimmed)
        if (rc !== region.sepColCount) {
          if (isLikelyTableTitleOrDesc(rowTrimmed)) {
            stopBuilding = true
            stopIdx = j
          } else {
            tableRows.push(normalizeRow(rowTrimmed))
          }
        } else {
          tableRows.push(normalizeRow(rowTrimmed))
        }
      }
      if (tableRows.length > 0) {
        if (out.length > 0 && out[out.length - 1].trim() !== '') out.push('')
        out.push(tableRows[0])
        const sepCols = Array(region.sepColCount).fill('---').join(' | ')
        out.push(`| ${sepCols} |`)
        for (let j = 1; j < tableRows.length; j++) {
          out.push(tableRows[j])
        }
        out.push('')
      }
      for (let k = stopIdx; k < region.regionEnd; k++) {
        const rb = lines[k].trim()
        if (isSepRow(rb)) continue
        if (isLikelyTableTitleOrDesc(rb)) {
          out.push(stripTableRowToText(rb))
        } else {
          out.push(lines[k])
        }
      }
      i = region.regionEnd
    } else {
      out.push(lines[i])
      i++
    }
  }
  return out.join('\n')
}

function isTableRow(line) {
  if (!line) return false
  return line.startsWith('|') && line.endsWith('|') && line.includes('|')
}

function isSepRow(line) {
  const cells = line.split('|').filter(c => c.trim())
  if (cells.length === 0) return false
  return cells.every(c => /^:?-+:?$/.test(c.trim()))
}

function normalizeRow(line) {
  let s = line.trim()
  if (!s.startsWith('|')) s = '| ' + s
  if (!s.endsWith('|')) s = s + ' |'
  const cells = s.split('|').slice(1, -1).map(c => c.trim()).filter(c => c.length > 0)
  return '| ' + cells.join(' | ') + ' |'
}

function removeHrRules(md) {
  const lines = md.split('\n')
  const out = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()
    if (isMarkdownHr(trimmed)) {
      const prevLine = i > 0 ? lines[i - 1].trim() : ''
      const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : ''
      const prevIsTableRow = prevLine.startsWith('|')
      const nextIsTableRow = nextLine.startsWith('|')
      const isBetweenTables = prevIsTableRow && nextIsTableRow
      if (!isBetweenTables) {
        i++
        continue
      }
    }
    out.push(line)
    i++
  }
  return out.join('\n')
}

function isMarkdownHr(line) {
  if (!line) return false
  if (!/^[-*_]{3,}$/.test(line)) return false
  const marker = line[0]
  return line.split(marker).every(c => c === '' || c === marker)
}

function escapeSingleTilde(md) {
  // Escape single ~ that are not part of ~~strikethrough~~ syntax
  return md.replace(/(?<!~)~(?!~)/g, '\\~')
}

export function renderMarkdown(content) {
  if (!content) return ''
  const normalizedTables = normalizeTables(content)
  const noHr = removeHrRules(normalizedTables)
  const escaped = escapeSingleTilde(noHr)
  const html = marked.parse(escaped)
  return DOMPurify.sanitize(html, {
    ADD_TAGS: ['code', 'pre', 'span'],
    ADD_ATTR: ['class', 'data-trace-index', 'data-chat-msg-id']
  })
}
